/** Presentational pieces shared by My tickets, the drawer and (Wave 4) the HR list and board. */
import {
  type Category,
  type ConfigLists,
  type EtaViewer,
  etaDisplay,
  findItem,
  fmtDate,
  fmtDateTime,
  type PeopleMap,
  type PersonRef,
  type Priority,
  STAGE_META,
  type Status,
  stageOf,
  type TicketDto,
  UNKNOWN_CATEGORY,
  UNKNOWN_PERSON_NAME,
} from '@hrms/shared'
import { LuLock } from 'react-icons/lu'
import { Avatar, Chip, Lozenge } from '../../../components/ui/index.ts'
import { cn } from '../../../lib/cn.ts'
import { hint } from '../../../lib/hint.ts'

export const UNKNOWN_STATUS_NAME = 'Unknown status'
export const UNKNOWN_PRIORITY_NAME = 'Unknown priority'

export function CategoryChip({
  categoryId,
  categories,
  dot,
  className,
}: {
  categoryId: string
  categories: readonly Category[]
  dot?: boolean
  className?: string
}) {
  const c = findItem(categories, categoryId) ?? UNKNOWN_CATEGORY
  return (
    <span
      className="inline-flex"
      {...hint(
        'archived' in c && c.archived
          ? `Category: ${c.name} (archived in Settings)`
          : `Category: ${c.name}`,
      )}
    >
      <Chip color={c.color} dot={dot} className={className}>
        {c.name}
      </Chip>
    </span>
  )
}

export function StatusLozenge({
  statusId,
  statuses,
  className,
}: {
  statusId: string
  statuses: readonly Status[]
  className?: string
}) {
  const s = findItem(statuses, statusId)
  const stage = s ? STAGE_META[s.stage] : null
  return (
    <span
      className="inline-flex"
      {...(stage ? hint(`${stage.label} stage. ${stage.description}`) : {})}
    >
      <Lozenge color={s?.color ?? 'slate'} className={className}>
        {s?.name ?? UNKNOWN_STATUS_NAME}
      </Lozenge>
    </span>
  )
}

/** Filled bars = round(1 + idx/(n-1)*3): Low 1, Medium 2, High 3, Urgent 4 (DESIGN.md §6). */
export function priorityLevel(priorities: readonly Priority[], priorityId: string): number {
  const idx = priorities.findIndex((p) => p.id === priorityId)
  if (idx < 0) return 0
  const n = priorities.length
  return n <= 1 ? 4 : Math.round(1 + (idx / (n - 1)) * 3)
}

export function PriorityBars({
  priorityId,
  priorities,
  showName = true,
  className,
}: {
  priorityId: string
  priorities: readonly Priority[]
  showName?: boolean
  className?: string
}) {
  const p = findItem(priorities, priorityId)
  const filled = priorityLevel(priorities, priorityId)
  const name = p?.name ?? UNKNOWN_PRIORITY_NAME
  return (
    <span
      data-color={p?.color ?? 'slate'}
      className={cn('inline-flex items-center gap-1.5', className)}
      {...(showName ? {} : hint(`${name} priority`))}
    >
      <span aria-hidden className="inline-flex items-end gap-0.5">
        {[5, 8, 10, 12].map((h, i) => (
          <span
            key={h}
            className={cn('w-[3px] rounded-[1px]', i < filled ? 'bg-cf' : 'bg-border-strong')}
            style={{ height: h }}
          />
        ))}
      </span>
      {showName ? (
        <span className="text-text">{name}</span>
      ) : (
        <span className="sr-only">{name}</span>
      )}
    </span>
  )
}

export function personName(people: PeopleMap, id: string | null | undefined): string {
  return (id && people[id]?.name) || UNKNOWN_PERSON_NAME
}

export function PersonAvatar({
  person,
  id,
  size,
  className,
}: {
  person: PersonRef | undefined
  /** Colour seed when the person is unknown. */
  id?: string
  size?: 'xs' | 'md' | 'lg'
  className?: string
}) {
  return (
    <Avatar
      name={person?.name ?? UNKNOWN_PERSON_NAME}
      seed={person?.id ?? id}
      src={person?.avatarUrl}
      size={size}
      className={className}
    />
  )
}

export function LockIcon({ className, employee }: { className?: string; employee?: boolean }) {
  const text = employee
    ? 'Confidential: only HR leads and your assigned HR partner can see it'
    : 'Confidential: only HR leads and the assignee can see it'
  return (
    <span className={cn('inline-flex text-amber-fg', className)} {...hint(text)}>
      <LuLock aria-label="Confidential" className="size-3.5" />
    </span>
  )
}

/** Expected resolution / ETA text, coloured for overdue. */
export function EtaText({
  ticket,
  config,
  viewer,
  now,
  className,
}: {
  ticket: TicketDto
  config: Pick<ConfigLists, 'priorities' | 'statuses'>
  viewer: EtaViewer
  now: number
  className?: string
}) {
  const eta = etaDisplay(ticket, { now, config, viewer })
  switch (eta.kind) {
    case 'done':
      return (
        <span className={className}>
          {eta.label}
          {eta.resolvedAt !== null && ` on ${fmtDate(eta.resolvedAt, { now })}`}
        </span>
      )
    case 'paused':
      return (
        <span
          className={cn('text-amber-fg', className)}
          {...hint(
            'The expected date is on hold until the employee replies, then moves out by the time spent waiting',
          )}
        >
          {eta.label}
        </span>
      )
    case 'overdue':
      return (
        <span
          className={cn('font-medium text-danger', className)}
          {...hint(`Was expected by ${fmtDateTime(eta.dueAt, { now })}`)}
        >
          {eta.label}
        </span>
      )
    case 'due':
      return (
        <span className={className} {...hint(fmtDateTime(eta.dueAt, { now }))}>
          {fmtDate(eta.dueAt, { now })}
        </span>
      )
    default:
      return <span className={cn('text-muted', className)}>{eta.label}</span>
  }
}

/** True when the ticket's status is in the Done stage. */
export function isDone(ticket: Pick<TicketDto, 'statusId'>, statuses: readonly Status[]) {
  return stageOf(statuses, ticket.statusId) === 'done'
}
